<template>
	<div :class="{ 'v-input--error': error }">
		<div
			v-if="label"
			:class="[$style.text600, , 'ao-c4 mx-2 mb-3']"
		>
			{{ label }}
		</div>
		<div
			v-if="showInputs"
			class="d-flex align-center pa-2 pt-0 w-100 mt-n4"
		>
			<!-- TODO: add formatting -->
			<ao-text-field
				:modelValue="formattedModelValue[0]"
				:readonly="!canChangeFromInput"
				density="compact"
				color="primary"
				variant="underlined"
				size="small"
				:rules="rulesMin"
				hide-details
				:suffix="componentPayload.appendInner"
				:error="error"
				class="w-100 pa-0 ma-0 pt-0 customize-input-icons-small"
				@update:modelValue="updateMinInput"
			/>
			<span class="mx-3 mt-3"> - </span>
			<ao-text-field
				:modelValue="formattedModelValue[1]"
				:readonly="!canChangeFromInput"
				color="primary"
				density="compact"
				variant="underlined"
				size="small"
				:rules="rulesMax"
				hide-details
				:suffix="componentPayload.appendInner"
				:error="error"
				class="w-100 pa-0 ma-0 pt-0 customize-input-icons-small"
				@update:modelValue="updateMaxInput"
			/>
		</div>
		<v-range-slider
			:class="numbered && $style.numbered"
			:modelValue="computedModelValue"
			hide-details
			:color="color"
			:density="density"
			:thumb-size="getThumbSize"
			:max="computedLimits.max"
			:min="computedLimits.min"
			:step="step"
			:error="error"
			@update:modelValue="
				(payload) => {
					onUpdate(payload)
				}
			"
		/>
		<div class="v-input__details">
			<div class="v-messages">
				<div
					v-if="error && errorMessages"
					class="v-messages__message"
				>
					<span v-if="!Array.isArray(errorMessages)">{{ errorMessages }}</span>
					<span v-else-if="!Array.isArray(errorMessages[0])">{{ errorMessages[0] }}</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { copyObject } from '@/helpers/utilsHelper'

export default {
	name: 'AoRangeSliders',

	props: {
		modelValue: {
			type: Array,
			default: [null, null],
		},

		label: {
			type: String,
			default: '',
		},

		density: {
			type: String,
			default: 'compact',
		},

		thumbSize: {
			type: Number,
			default: 16,
		},

		numbered: {
			type: Boolean,
			default: false,
		},

		color: {
			type: String,
			default: 'primary',
		},

		min: Number,

		max: Number,

		step: {
			type: Number,
			default: 1,
		},

		showInputs: Boolean,

		canChangeFromInput: {
			type: Boolean,
			default: true,
		},

		valueFormatterCallback: {
			type: Function,
			default: undefined,
		},

		componentPayload: {
			type: Object,
			default: () => ({}),
		},

		showOnlyIntegers: {
			type: Boolean,
			default: true,
		},

		error: {
			type: Boolean,
			default: false,
		},

		errorMessages: {
			type: Array || String,
			default: [null],
		},

		// Return null if min or max equal to value
		returnNullable: {
			type: Boolean,
			default: true,
		}
	},

	emits: ['update:modelValue'],

	data() {
		return {
			range: this.modelValue,

			inputMin: this.modelValue[0],
			inputMax: this.modelValue[1],
		}
	},

	computed: {
		rulesMin() {
			return [
				// v => v <= this.inputMax || 'Not grow than max',
				// v => v >= this.min || 'Not less than min'
			]
		},
		rulesMax() {
			return [
				// v => v <= this.max || 'Not grow than max',
				// v => v >= this.inputMin || 'Not less than min'
			]
		},
		maxSliderValue() {
			let max = Math.ceil(this.max)
			return max
		},

		computedLimits() {
			if (this.showOnlyIntegers) {
				return {
					min: Math.floor(this.min),
					max: Math.ceil(this.max),
				}
			}
			return {
				min: this.min,
				max: this.max,
			}
		},

		computedModelValue() {
			let [min, max] = copyObject(this.modelValue)
			if (min === null) min = this.computedLimits.min
			if (max === null) max = this.computedLimits.max
			return [min, max]
		},

		formattedModelValue() {
			return this.computedModelValue.map((value) => {
				if (value === null) return null
				return this.valueFormatterCallback ? this.valueFormatterCallback(value) : value
			})
		},

		getThumbSize() {
			return this.numbered ? 24 : this.thumbSize
		},
	},

	watch: {
		inputMin(val) {
			if (val > this.inputMax || val < this.min) {
				return false
			}
			this.range[0] = val
		},
		inputMax(val) {
			if (val < this.inputMin || val > this.max) {
				return
			}
			this.range[1] = val
		},
		range: {
			deep: true,
			handler() {
				let [min, max] = this.range
				this.inputMax = max
				this.inputMin = min
				min = (this.min === min || this.computedLimits.min === min) ? null : min
				max = (this.max === max || this.computedLimits.max === max) ? null : max

				this.$emit('update:modelValue', [min, max])
			},
		},
	},

	methods: {
		onUpdate([min, max]) {
			let returnedMin = min
			let returnedMax = max

			if ((this.min === returnedMin || this.computedLimits.min === returnedMin) && this.returnNullable) returnedMin = null
			if ((this.max === returnedMax || this.computedLimits.max === returnedMax) && this.returnNullable) returnedMax = null

			this.$emit('update:modelValue', [returnedMin, returnedMax])
		},

		updateMinInput(value) {
			let returnedMin = value
			let returnedMax = this.computedModelValue[1]

			if (returnedMin <= this.min && this.returnNullable) returnedMin = null
			if (returnedMax >= this.max && this.returnNullable) returnedMax = null

			this.$emit('update:modelValue', [Number(returnedMin), returnedMax])
		},

		updateMaxInput(value) {
			let returnedMin = this.computedModelValue[0]
			let returnedMax = value

			if (returnedMin <= this.min && this.returnNullable) returnedMin = null
			if (returnedMax >= this.max && this.returnNullable) returnedMax = null

			this.$emit('update:modelValue', [returnedMin, Number(returnedMax)])
		},
	},
}
</script>

<style module lang="scss">
.text600 {
	color: #86919d;
}
.numbered {
	:global(.v-slider-thumb:after) {
		content: attr(aria-valuenow);

		position: absolute;
		top: 0;

		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;

		font-family: Inter, sans-serif;
		font-weight: 500;
		font-size: 11px;
		color: white;
	}
}
</style>
